import { Suspense, useEffect } from 'react'
import { RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryRetryDelay, shouldRetryQuery } from './api/queryRetry'
import { useTranslation } from 'react-i18next'
import { AuthProvider } from './providers/AuthProvider'
import { router } from './routes/router'
import { ThemeProvider, useTheme } from './hooks/useTheme'
import DiagSpinner from './components/diagnostics/DiagSpinner'
import { Toaster } from './components/ui/toast'
import './i18n/config'
import './styles/global.css'

// TanStack Query client configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // Data fresh for 30s
      gcTime: 5 * 60 * 1000, // Keep unused data for 5min
      retry: shouldRetryQuery, // Limited retries, never for a timeout or a cancelled request
      retryDelay: queryRetryDelay, // Longer jittered backoff for BDI-5010 (analytics busy)
      refetchOnWindowFocus: false, // Don't refetch on window focus
    },
  },
})

function AppContent() {
  const { theme } = useTheme()
  const { i18n } = useTranslation()
  const isAr = i18n.language === 'ar'

  // Keep <html lang> and document direction in sync with i18n.
  useEffect(() => {
    document.documentElement.lang = i18n.language
    document.dir = isAr ? 'rtl' : 'ltr'
  }, [i18n.language, isAr])

  return (
    <>
      <Suspense
        fallback={
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <DiagSpinner size="large" />
          </div>
        }
      >
        <RouterProvider router={router} />
      </Suspense>

      <Toaster
        theme={theme}
        position="top-right"
        richColors
        closeButton
        dir={isAr ? 'rtl' : 'ltr'}
      />

      {/* React Query Devtools (dev only) */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
